import { InputBase, NativeSelect } from '@mui/material';
import { ICellEditorParams } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { KeyboardEvent, forwardRef, memo, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ProductCategoryDto, ProductGroupDto } from '../../PricePrgClient';

const KEY_BACKSPACE = 'Backspace';
const KEY_DELETE = 'Delete';
const KEY_F2 = 'F2';
const KEY_ENTER = 'Enter';
const KEY_TAB = 'Tab';

export const NumericEditor = memo(
    forwardRef((props: ICellEditorParams, ref) => {
        const createInitialState = () => {
            let startValue;
            let highlightAllOnFocus = true;

            if (props.eventKey === KEY_BACKSPACE || props.eventKey === KEY_DELETE) {
                // if backspace or delete pressed, we clear the cell
                startValue = '';
            } else if (props.eventKey) {
                // if a letter was pressed, we start with the letter
                startValue = props.eventKey;
                highlightAllOnFocus = false;
            } else {
                // otherwise we start with the current value
                startValue = props.value;
                if (props.eventKey === KEY_F2) {
                    highlightAllOnFocus = false;
                }
            }

            return {
                value: startValue,
                highlightAllOnFocus
            };
        };

        const initialState = createInitialState();
        const [value, setValue] = useState(initialState.value);
        const [highlightAllOnFocus, setHighlightAllOnFocus] = useState(initialState.highlightAllOnFocus);
        const refInput = useRef<HTMLInputElement>(null);

        // focus on the input
        useEffect(() => {
            // get ref from React component
            const eInput = refInput.current!;
            eInput.focus();
            if (highlightAllOnFocus) {
                eInput.select();

                setHighlightAllOnFocus(false);
            } else {
                // when we started editing, we want the caret at the end, not the start.
                // this comes into play in two scenarios:
                //   a) when user hits F2
                //   b) when user hits a printable character
                const length = eInput.value ? eInput.value.length : 0;
                if (length > 0) {
                    eInput.setSelectionRange(length, length);
                }
            }
        }, [refInput, highlightAllOnFocus]);

        /* Utility Methods */
        const cancelBeforeStart = props.eventKey && '1234567890'.indexOf(props.eventKey) < 0;

        const isLeftOrRight = (event: KeyboardEvent) => {
            return ['ArrowLeft', 'ArrowLeft'].indexOf(event.key) > -1;
        };

        const isCharNumeric = (charStr: string) => {
            return !!/\d/.test(charStr);
        };

        const isKeyPressedNumeric = (event: KeyboardEvent) => {
            const charStr = event.key;
            return isCharNumeric(charStr);
        };

        const deleteOrBackspace = (event: KeyboardEvent) => {
            return [KEY_DELETE, KEY_BACKSPACE].indexOf(event.key) > -1;
        };

        const finishedEditingPressed = (event: KeyboardEvent) => {
            const key = event.key;
            return key === KEY_ENTER || key === KEY_TAB;
        };

        const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
            if (isLeftOrRight(event) || deleteOrBackspace(event)) {
                event.stopPropagation();
                return;
            }

            if (!finishedEditingPressed(event) && !isKeyPressedNumeric(event)) {
                if (event.preventDefault) event.preventDefault();
            }

            if (finishedEditingPressed(event)) {
                props.stopEditing();
            }
        };

        /* Component Editor Lifecycle methods */
        useImperativeHandle(ref, () => {
            return {
                // the final value to send to the grid, on completion of editing
                getValue() {
                    return value;
                },

                // Gets called once before editing starts, to give editor a chance to
                // cancel the editing before it even starts.
                isCancelBeforeStart() {
                    return cancelBeforeStart;
                },

                // Gets called once when editing is finished (eg if Enter is pressed).
                // If you return true, then the result of the edit will be ignored.
                isCancelAfterEnd() {
                    // will reject the number if it greater than 1,000,000
                    // not very practical, but demonstrates the method.
                    return value > 1000000;
                }
            };
        });

        return (
            <input
                ref={refInput}
                value={value}
                onChange={event => setValue(event.target.value)}
                onKeyDown={onKeyDown}
                style={{ width: '100%' }}
            />
        );
    })
);

interface IProductCategoryEditorParams extends ICellEditorParams {
    productCategories: ProductCategoryDto[];
    allowNull: boolean;
}
export const ProductCategoryEditor = memo(
    forwardRef((props: IProductCategoryEditorParams, ref) => {
        const [value, setValue] = useState(props.value ?? null);
        const refInput = useRef(null);
        const [productCategories, setProductCategories] = useState([] as ProductCategoryDto[]);
        const [allowNull, setAllowNull] = useState(false);
        const handleSetValue = (value: string | number | null) => {
            if (typeof value == 'string') {
                if (value) {
                    setValue(parseInt(value as string));
                } else {
                    setValue(null);
                }
            } else {
                setValue(value);
            }
        };

        useEffect(() => {
            setProductCategories(props.productCategories);
        }, [props.productCategories]);
        useEffect(() => {
            setAllowNull(props.allowNull ?? false);
        }, [props.allowNull]);

        /* Component Editor Lifecycle methods */
        useImperativeHandle(ref, () => {
            return {
                // the final value to send to the grid, on completion of editing
                getValue() {
                    // this simple editor doubles any value entered into the input
                    return value;
                },

                // Gets called once before editing starts, to give editor a chance to
                // cancel the editing before it even starts.
                isCancelBeforeStart() {
                    return false;
                },

                // Gets called once when editing is finished (eg if Enter is pressed).
                // If you return true, then the result of the edit will be ignored.
                isCancelAfterEnd() {
                    // our editor will reject any value greater than 1000
                    return false;
                }
            };
        });
        return (
            <NativeSelect
                value={value ?? ''}
                fullWidth
                input={<InputBase sx={{ fontSize: '2rem' }} />}
                sx={{ height: '20px', width: 'auto' }}
                ref={refInput}
                onChange={event => handleSetValue(event.target.value)}
            >
                {allowNull && (
                    <option key={0} value={''}>
                        &nbsp;
                    </option>
                )}
                {productCategories.map(c => (
                    <option key={c.productCategoryId} value={c.productCategoryId}>
                        {c.name}
                    </option>
                ))}
            </NativeSelect>
        );
    })
);

interface IPalletTypeEditorParams extends ICellEditorParams {}

export const PalletTypeEditor = memo(
    forwardRef((props: IPalletTypeEditorParams, ref) => {
        const [value, setValue] = useState(props.value ?? null);
        const refInput = useRef(null);

        const handleSetValue = (value: string | number | null) => {
            if (typeof value == 'string') {
                if (value) {
                    setValue(parseInt(value as string));
                } else {
                    setValue(null);
                }
            } else {
                setValue(value);
            }
        };
        /* Component Editor Lifecycle methods */
        useImperativeHandle(ref, () => {
            return {
                // the final value to send to the grid, on completion of editing
                getValue() {
                    // this simple editor doubles any value entered into the input
                    return value;
                },

                // Gets called once before editing starts, to give editor a chance to
                // cancel the editing before it even starts.
                isCancelBeforeStart() {
                    return false;
                },

                // Gets called once when editing is finished (eg if Enter is pressed).
                // If you return true, then the result of the edit will be ignored.
                isCancelAfterEnd() {
                    // our editor will reject any value greater than 1000
                    return false;
                }
            };
        });

        return (
            <NativeSelect
                value={value ?? ''}
                fullWidth
                input={<InputBase sx={{ fontSize: '2rem' }} />}
                sx={{ height: '20px', width: 'auto' }}
                ref={refInput}
                onChange={event => handleSetValue(event.target.value)}
            >
                <option key={0} value={0}>
                    EUR
                </option>
                <option key={1} value={1}>
                    SPA
                </option>
            </NativeSelect>
        );
    })
);

interface IProductGroupEditorParams extends ICellEditorParams {
    productGroups: ProductGroupDto[];
}

export const ProductGroupEditor = memo(
    forwardRef((props: IProductGroupEditorParams, ref) => {
        const [value, setValue] = useState(props.value ?? null);
        const refInput = useRef(null);
        const [productGroups, setProductGroups] = useState([] as ProductGroupDto[]);
        const handleSetValue = (value: string | number | null) => {
            if (typeof value == 'string') {
                if (value) {
                    setValue(parseInt(value as string));
                } else {
                    setValue(null);
                }
            } else {
                setValue(value);
            }
        };

        useEffect(() => {
            setProductGroups(props.productGroups);
        }, [props.productGroups]);

        /* Component Editor Lifecycle methods */
        useImperativeHandle(ref, () => {
            return {
                // the final value to send to the grid, on completion of editing
                getValue() {
                    // this simple editor doubles any value entered into the input
                    return value;
                },

                // Gets called once before editing starts, to give editor a chance to
                // cancel the editing before it even starts.
                isCancelBeforeStart() {
                    return false;
                },

                // Gets called once when editing is finished (eg if Enter is pressed).
                // If you return true, then the result of the edit will be ignored.
                isCancelAfterEnd() {
                    // our editor will reject any value greater than 1000
                    return false;
                }
            };
        });
        return (
            <NativeSelect
                value={value ?? ''}
                fullWidth
                input={<InputBase sx={{ fontSize: '2rem' }} />}
                sx={{ height: '20px', width: 'auto' }}
                ref={refInput}
                onChange={event => handleSetValue(event.target.value)}
            >
                <option key={0} value={''}>
                    &nbsp;
                </option>

                {productGroups.map(c => (
                    <option key={c.productGroupId} value={c.productGroupId}>
                        {c.name}
                    </option>
                ))}
            </NativeSelect>
        );
    })
);

export const TextEditor = memo(
    forwardRef((props: ICellEditorParams, ref) => {
        const [value, setValue] = useState(parseInt(props.value));
        const refInput = useRef<HTMLInputElement>(null);

        useEffect(() => {
            // focus on the input
            refInput.current?.focus();
        }, []);

        /* Component Editor Lifecycle methods */
        useImperativeHandle(ref, () => {
            return {
                // the final value to send to the grid, on completion of editing
                getValue() {
                    // this simple editor doubles any value entered into the input
                    return value * 2;
                },

                // Gets called once before editing starts, to give editor a chance to
                // cancel the editing before it even starts.
                isCancelBeforeStart() {
                    return false;
                },

                // Gets called once when editing is finished (eg if Enter is pressed).
                // If you return true, then the result of the edit will be ignored.
                isCancelAfterEnd() {
                    // our editor will reject any value greater than 1000
                    return value > 1000;
                }
            };
        });

        return (
            <input
                type="text"
                ref={refInput}
                value={value}
                onChange={event => setValue(parseInt(event.target.value))}
                style={{ width: '100%' }}
            />
        );
    })
);
